import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { useInfiniteQueryWithLimit } from '../hooks/useInfiniteQueryWithLimit';
import { useRefetchQuery } from '../hooks/useRefetchQuery';
import { queryKeys } from '../queryKeys';
import { composePaginatedData } from '../utils';

import { getContinueWatchingSettings } from './getContinueWatchingSettings';
import { createContinueWatchingQueryOptions } from './queryOptions';
import type { WatchedAsset } from './types';

type UseContinueWatchingProps = {
  useLimit?: boolean;
  pageSize: number;
  referenceId: string;
};

export function useContinueWatching({ referenceId, pageSize, useLimit }: UseContinueWatchingProps) {
  const category = useCategoryTreeItem(referenceId);
  const limit = useLimit ? category?.railProperty.railItemsLimit : undefined;
  const enabled = Boolean(category);

  const {
    data,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch: refetchContinueWatching,
  } = useInfiniteQueryWithLimit<WatchedAsset>({
    ...(enabled
      ? createContinueWatchingQueryOptions({
          pageSize,
          referenceId,
          settings: getContinueWatchingSettings(category!),
        })
      : { queryKey: queryKeys.disabled }),
    enabled,
    pageSize,
    limit,
  });

  const items = useMemo(() => {
    if (!data) {
      return [];
    }

    return composePaginatedData({ data, limit });
  }, [data, limit]);

  const refetch = useRefetchQuery({ enabled, refetch: refetchContinueWatching });

  const totalCount = data ? data.pages[0].totalCount : 0;

  return {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
}
